import React from 'react';
import { Helmet } from 'react-helmet-async';
import { getHrefLangPaths } from '../config/localeRoutes';

const DEFAULTS = {
  fr: {
    title: 'Leagora : infrastructures vidéo métier WebRTC pour entreprises',
    description:
      'Leagora déploie des infrastructures vidéo métier WebRTC : assistance, RDV, live, API, hébergement France et intégration sur mesure.',
  },
  en: {
    title: 'Leagora | WebRTC business video infrastructure for enterprises',
    description:
      'Leagora designs and deploys WebRTC video infrastructure: support, appointments, live, API, France hosting, custom integration.',
  },
};

function SEO({
  title,
  description,
  canonical = '/',
  ogType = 'website',
  ogImage = null,
  noIndex = false,
  locale = 'fr',
  articlePublishedTime = null,
  articleModifiedTime = null,
}) {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://leagora.io';
  const siteName = process.env.REACT_APP_SITE_NAME || 'Leagora';
  const twitterSite = process.env.REACT_APP_TWITTER_SITE || '';
  const defaults = DEFAULTS[locale] || DEFAULTS.fr;
  const resolvedTitle = title || defaults.title;
  const resolvedDescription = description || defaults.description;
  const defaultOgImage = `${siteUrl}/og-image.png`;
  const normalizedCanonical = canonical.startsWith('http')
    ? canonical
    : `${siteUrl}${canonical.startsWith('/') ? canonical : `/${canonical}`}`;
  const fullUrl = normalizedCanonical.endsWith('/') || normalizedCanonical.includes('?')
    ? normalizedCanonical
    : `${normalizedCanonical}/`;
  const finalOgImage = ogImage || defaultOgImage;
  const normalizedTwitterSite = twitterSite.startsWith('@')
    ? twitterSite
    : twitterSite
      ? `@${twitterSite.replace(/^@/, '')}`
      : '';
  const hrefLangBasePath = canonical.startsWith('http')
    ? new URL(canonical).pathname
    : canonical.startsWith('/')
      ? canonical
      : `/${canonical}`;
  const hrefLangPaths = getHrefLangPaths(hrefLangBasePath);
  const xDefaultUrl = `${siteUrl}${hrefLangPaths.xDefault}`;
  const ogLocale = locale === 'en' ? 'en_US' : 'fr_FR';
  const ogLocaleAlternate =
    hrefLangPaths.alternateLocales.length > 1
      ? locale === 'en'
        ? 'fr_FR'
        : 'en_US'
      : null;
  const articlePublished = articlePublishedTime || articleModifiedTime;
  const articleModified = articleModifiedTime || articlePublishedTime;

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      {!noIndex ? (
        <>
          <link rel="canonical" href={fullUrl} />
          {hrefLangPaths.fr ? (
            <link rel="alternate" hrefLang="fr" href={`${siteUrl}${hrefLangPaths.fr}`} />
          ) : null}
          {hrefLangPaths.en ? (
            <link rel="alternate" hrefLang="en" href={`${siteUrl}${hrefLangPaths.en}`} />
          ) : null}
          <link rel="alternate" hrefLang="x-default" href={xDefaultUrl} />
        </>
      ) : null}

      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:image:secure_url" content={finalOgImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={resolvedTitle} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={ogLocale} />
      {ogLocaleAlternate ? (
        <meta property="og:locale:alternate" content={ogLocaleAlternate} />
      ) : null}
      {ogType === 'article' && articlePublished ? (
        <meta property="article:published_time" content={articlePublished} />
      ) : null}
      {ogType === 'article' && articleModified ? (
        <meta property="article:modified_time" content={articleModified} />
      ) : null}
      {ogType === 'article' ? (
        <meta property="article:author" content={siteName} />
      ) : null}

      <meta name="twitter:card" content="summary_large_image" />
      {normalizedTwitterSite ? <meta name="twitter:site" content={normalizedTwitterSite} /> : null}
      {normalizedTwitterSite ? <meta name="twitter:creator" content={normalizedTwitterSite} /> : null}
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={finalOgImage} />
      <meta name="twitter:image:src" content={finalOgImage} />
      <meta name="twitter:image:alt" content={resolvedTitle} />

      <meta
        name="robots"
        content={
          noIndex
            ? 'noindex, nofollow'
            : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
        }
      />
      <meta name="language" content={locale} />
      <meta name="author" content={siteName} />
      <meta name="copyright" content={siteName} />

      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={siteName} />

      <meta name="format-detection" content="telephone=yes" />
      <meta name="format-detection" content="email=yes" />
      <meta name="format-detection" content="address=yes" />
    </Helmet>
  );
}

export default SEO;
